import { useInView, useSpring } from 'framer-motion';
import { useContext, useEffect, useRef, useState } from 'react';

import { SSGDataContext } from '@/helpers/contexts';
import { getDecimalSeparator } from '@/helpers/locales';

const dotAsDecimalSeparatorRegex = /[+-]?(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?/;
const commaAsDecimalSeparatorRegex =
  /[+-]?(?:\d{1,3}(?:\.\d{3})*|\d+)(?:,\d+)?/;

const AnimatedCounter = ({ text }: { text: string }) => {
  const { language } = useContext(SSGDataContext);
  const [decimal, setDecimal] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [displayTemplate, setDisplayTemplate] = useState('');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const springCount = useSpring(0, {
    bounce: 0,
    duration: 2000,
  });

  useEffect(() => {
    springCount.on('change', (value) => {
      if (decimal > 0) {
        setDisplayCount(Math.round(value * (decimal * 10)) / (decimal * 10));
      } else {
        setDisplayCount(Math.floor(value));
      }
    });
  }, [springCount, decimal]);

  useEffect(() => {
    const decimalSeparator = getDecimalSeparator(language);
    let templateString = '';
    if (decimalSeparator === ',') {
      templateString = text.replace(commaAsDecimalSeparatorRegex, '{value}');
    } else {
      templateString = text.replace(dotAsDecimalSeparatorRegex, '{value}');
    }

    setDisplayTemplate(templateString);
  }, [text]);

  useEffect(() => {
    if (isInView) {
      const decimalSeparator = getDecimalSeparator(language);
      const res = text.match(
        decimalSeparator === ','
          ? commaAsDecimalSeparatorRegex
          : dotAsDecimalSeparatorRegex,
      );

      if (res) {
        const stringValue =
          getDecimalSeparator(language) === ','
            ? res[0].replace('.', '').replace(',', '.')
            : res[0].replace(',', '');
        const number = parseFloat(stringValue);
        setDecimal(stringValue.split('.')[1]?.length);
        springCount.set(number);
      }
    }
  }, [isInView, text]);

  return (
    <div ref={ref} className="will-change-contents">
      {displayTemplate.replace(
        '{value}',
        displayCount.toLocaleString(language),
      )}
    </div>
  );
};

export default AnimatedCounter;
